import {
    ApplicationConfig,
    importProvidersFrom,
    inject,
    InjectionToken,
    provideAppInitializer,
    provideBrowserGlobalErrorListeners,
} from '@angular/core';
import {
    PreloadAllModules,
    provideRouter,
    RouteReuseStrategy,
    withPreloading,
    withRouterConfig,
    withViewTransitions,
} from '@angular/router';
import {
    HttpContextToken,
    provideHttpClient,
    withFetch,
    withInterceptors,
    withInterceptorsFromDi,
} from '@angular/common/http';
import { authInterceptor } from '@core/interceptors/auth.interceptor';
import { timeoutInterceptor } from '@core/interceptors/timeout.interceptor';
import { CustomReuseStrategy } from '@core/services/reuse-strategy.service';
import { routes } from '@routes/app.routes';
import { keycloakProvider } from '@/app/keycloak.config';
import { MAT_SNACK_BAR_DEFAULT_OPTIONS } from '@angular/material/snack-bar';
import { LostNetworkService } from '@services/lost-network.service';
import { MAT_TABS_CONFIG } from '@angular/material/tabs';
import { MAT_FORM_FIELD_DEFAULT_OPTIONS } from '@angular/material/form-field';
import { provideNativeDateAdapter } from '@angular/material/core';
import { CurrentService } from '@services/current.service';
import { LucideAngularModule } from 'lucide-angular';
import { lucideIcons } from '@/app/lucide-icons';
import { provideToastr } from 'ngx-toastr';
import { AbstractSecurityStorage } from 'angular-auth-oidc-client';
import { TabAwareSecurityStorage } from '@services/tab-aware-auth';
import { loadingInterceptor } from '@core/interceptors/loading.interceptor';

export const PERSON_IAS_SPORTSMAN = new InjectionToken<string>('person-ias-sportsman');
export const COMPETITION_IAS_URL = new InjectionToken<string>('competition-ias-url');

export const FIO_PREFIX = new InjectionToken('FIO_PREFIX');
export const POSITION_PREFIX = new InjectionToken('POSITION_PREFIX');

export const SKIP_LOADING_CONTEXT = new HttpContextToken<boolean>(() => false);

export const appConfig: ApplicationConfig = {
    providers: [
        provideBrowserGlobalErrorListeners(),
        provideRouter(
            routes,
            withPreloading(PreloadAllModules),
            withRouterConfig({ paramsInheritanceStrategy: 'always' }),
            withViewTransitions(),
        ),
        provideHttpClient(
            withFetch(),
            withInterceptorsFromDi(),
            withInterceptors([authInterceptor, timeoutInterceptor, loadingInterceptor]),
        ),
        provideToastr({
            timeOut: 5000,
            positionClass: 'toast-top-right',
            preventDuplicates: true,
            extendedTimeOut: 500,
        }),
        keycloakProvider(false),
        // Токены — в localStorage, общем для вкладок; вход и обновление токена, идущие во вкладке, —
        // в её sessionStorage, иначе вкладки сбивают друг другу state и теряют вход (#29300).
        {
            provide: AbstractSecurityStorage,
            useClass: TabAwareSecurityStorage,
        },
        importProvidersFrom(LucideAngularModule.pick(lucideIcons)),
        {
            provide: MAT_SNACK_BAR_DEFAULT_OPTIONS,
            useValue: {
                duration: 2000,
                horizontalPosition: 'right',
                verticalPosition: 'top',
                panelClass: ['snack-bar'],
            },
        },
        { provide: RouteReuseStrategy, useClass: CustomReuseStrategy },
        { provide: PERSON_IAS_SPORTSMAN, useValue: 'https://ias.sport.mos.ru/persons/' },
        { provide: COMPETITION_IAS_URL, useValue: 'https://ias.sport.mos.ru/events/' },
        { provide: MAT_TABS_CONFIG, useValue: { animationDuration: '0ms' } },
        {
            provide: MAT_FORM_FIELD_DEFAULT_OPTIONS,
            useValue: {
                subscriptSizing: 'dynamic',
            },
        },
        provideNativeDateAdapter(),
        LostNetworkService,
        provideAppInitializer(() => {
            inject(LostNetworkService);
            inject(CurrentService);
        }),
        { provide: FIO_PREFIX, useValue: 'mks_approver_name' },
        { provide: POSITION_PREFIX, useValue: 'mks_approver_position' },
    ],
};
